// TO DO: Add Clarity React for improved monitoring/identification of users

/**
 * Serializes a context object for New Relic. Primitive values are passed through
 * as-is; non-primitives (arrays, plain objects) are JSON-stringified so the
 * information is preserved rather than silently dropped.
 *
 * The `error` key is intentionally excluded here — callers should pass Error
 * instances via the dedicated `ctxError` parameter of logError so NR receives the
 * original stack trace rather than a synthetic wrapper.
 */
function sanitizeAttrs(ctx: Record<string, unknown>): Record<string, string | number | boolean> | undefined {
  const entries: [string, string | number | boolean][] = []
  for (const [k, v] of Object.entries(ctx)) {
    if (v === null || v === undefined) continue
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      entries.push([k, v])
    } else {
      entries.push([k, JSON.stringify(v)])
    }
  }
  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

/**
 * Logs an error to the configured monitoring provider.
 *
 * When `ctx` contains an `error` key that is an Error instance, that object is
 * forwarded directly to New Relic so the original message and stack trace are
 * preserved. All other context values are serialized via sanitizeAttrs.
 */
export function logError(msg: string, ctx?: Record<string, unknown>) {
  const { error: ctxError, ...rest } = ctx ?? {}
  const errorObj = ctxError instanceof Error ? ctxError : new Error(msg)
  window.newrelic?.noticeError(errorObj, sanitizeAttrs(rest))
}

/** Records a breadcrumb event to the configured monitoring provider. */
export function logBreadcrumb(msg: string, ctx?: Record<string, unknown>) {
  window.newrelic?.addPageAction(msg, ctx ? sanitizeAttrs(ctx) : undefined)
}

export interface ConsumerNoPii {
  consumerId: string
  agreedToTermsAndConditions?: boolean | null
  latestTermsVersionAgreedTo?: string | null
  submittedAppReviewDate: string | null
  firebaseId?: string
}

/**
 * Sends consumer identity to New Relic (no PII — consumerId only).
 *
 * `persist: true` ensures the attribute survives hard reloads and is attached to
 * all subsequent NR events for the session, not just the current page load.
 */
export function identifyMetricUser(user: ConsumerNoPii) {
  window.newrelic?.setCustomAttribute('consumerId', user.consumerId, true)
}
